import { ChevronDown, ChevronUp, Download, PlayCircle, RefreshCcw } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

import type { SoftwareAssuranceWorkflowController } from '@/hooks/workflow/useSoftwareAssuranceWorkflow'

interface ScansStepSectionProps {
  workflow: SoftwareAssuranceWorkflowController
}

export function ScansStepSection({ workflow }: ScansStepSectionProps) {
  return (
    <div className="space-y-4">
      <h4 className="text-sm font-semibold">Execute Scans</h4>
      <p className="text-sm text-muted-foreground">
        Launch scans against the selected target using configured tools.
      </p>
      <Input
        placeholder="Target path (required for scan execution)"
        value={workflow.targetPath}
        onChange={(event) => workflow.setTargetPath(event.target.value)}
      />
      <Button onClick={workflow.handleRunTools}>
        Run Selected Tools
        <PlayCircle className="ml-2 h-4 w-4" />
      </Button>
      {workflow.runLog.length > 0 && (
        <div className="rounded-lg border border-border bg-muted/30 p-3 text-xs space-y-1">
          {workflow.runLog.map((line, index) => (
            <p key={`${line}-${index}`}>{line}</p>
          ))}
        </div>
      )}
      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={() => workflow.setShowRunDrawer((prev) => !prev)}>
          {workflow.showRunDrawer ? 'Hide Run Status' : 'View Run Status'}
          {workflow.showRunDrawer ? (
            <ChevronUp className="ml-2 h-4 w-4" />
          ) : (
            <ChevronDown className="ml-2 h-4 w-4" />
          )}
        </Button>
        <Button variant="ghost" size="sm" onClick={() => workflow.refetchToolRuns()}>
          <RefreshCcw className="mr-2 h-4 w-4" />
          Refresh
        </Button>
      </div>
      {workflow.showRunDrawer && (
        <div className="rounded-lg border border-border bg-muted/10 p-4 space-y-3">
          {workflow.toolRuns.length === 0 && (
            <p className="text-xs text-muted-foreground">No tool runs recorded yet.</p>
          )}
          {workflow.toolRuns.map((run) => {
            const artifacts = workflow.getRunArtifacts(run)
            return (
              <div key={run.run_id} className="rounded-lg border border-border p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">Run {run.run_id.slice(0, 8)}</p>
                    <p className="text-xs text-muted-foreground">Tool {run.tool_id}</p>
                  </div>
                  <Badge
                    variant={
                      run.status === 'completed'
                        ? 'default'
                        : run.status === 'failed'
                        ? 'destructive'
                        : 'secondary'
                    }
                  >
                    {run.status}
                  </Badge>
                </div>
                {artifacts.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Artifacts</p>
                    <div className="flex flex-wrap gap-2">
                      {artifacts.map((artifact) => (
                        <Button
                          key={`${run.run_id}-${artifact.index}`}
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            workflow.handleDownloadArtifact(
                              run.run_id,
                              artifact.index,
                              artifact.filename
                            )
                          }
                        >
                          <Download className="mr-2 h-3 w-3" />
                          {artifact.filename}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
